import { useCallback, useRef } from 'react';
import { DEFAULT_PAGE } from '../../../constants/pagination';

const normalizeSearchText = (value) => (value ?? '').toString().trim();

export const usePostingTableControls = ({ filters, setFilters, resetFilters, dispatch, sanitizeSearchText }) => {
  const lastPageRef = useRef(filters.pageNumber);
  const lastPerPageRef = useRef(filters.rowsPerPage);

  const handlePageChange = useCallback(
    (currentPage) => {
      if (Number(currentPage) === Number(lastPageRef.current)) return;
      lastPageRef.current = currentPage;
      dispatch(setFilters({ ...filters, pageNumber: currentPage }));
    },
    [dispatch, filters, setFilters],
  );

  const handlePerPageChange = useCallback(
    (newPerPage) => {
      if (Number(newPerPage) === Number(lastPerPageRef.current)) return;
      lastPerPageRef.current = newPerPage;
      lastPageRef.current = DEFAULT_PAGE;
      dispatch(setFilters({ ...filters, rowsPerPage: newPerPage, pageNumber: DEFAULT_PAGE }));
    },
    [dispatch, filters, setFilters],
  );

  const handleSearch = useCallback(
    (params) => {
      lastPageRef.current = DEFAULT_PAGE;
      const sanitizedSearchText = sanitizeSearchText ? sanitizeSearchText(params) : normalizeSearchText(params);
      dispatch(setFilters({ ...filters, searchText: sanitizedSearchText, pageNumber: DEFAULT_PAGE }));
    },
    [dispatch, filters, sanitizeSearchText, setFilters],
  );

  const handleResetSearch = useCallback(() => {
    lastPageRef.current = DEFAULT_PAGE;
    dispatch(setFilters({ ...filters, searchText: '', pageNumber: DEFAULT_PAGE }));
  }, [dispatch, filters, setFilters]);

  const handleApplyFilter = useCallback(
    (filterValues) => {
      lastPageRef.current = DEFAULT_PAGE;
      dispatch(setFilters({ ...filters, ...filterValues, pageNumber: DEFAULT_PAGE }));
    },
    [dispatch, filters, setFilters],
  );

  const handleResetFilter = useCallback(() => {
    lastPageRef.current = DEFAULT_PAGE;
    dispatch(resetFilters());
  }, [dispatch, resetFilters]);

  return {
    handlePageChange,
    handlePerPageChange,
    handleSearch,
    handleResetSearch,
    handleApplyFilter,
    handleResetFilter,
  };
};
